/**
 * XLR8 Analytics Service - Mixpanel Integration
 *
 * PURPOSE:
 *   Wraps Mixpanel SDK for consistent event tracking across the app.
 *   Handles identity management (anonymous → identified users) and
 *   provides a simple, fire-and-forget interface for tracking events.
 *
 * USAGE:
 *   import analytics from './services/analytics';
 *
 *   // Track an event
 *   analytics.track('Request Created', { genre: 'Hip Hop', mood: 'dark' });
 *
 *   // Identify a user (call on login)
 *   analytics.identify(userId, { email, artist_name, role });
 *
 *   // Reset on logout
 *   analytics.reset();
 *
 * IDENTITY MANAGEMENT (Critical for Mixpanel):
 *   - Anonymous users get a device_id automatically
 *   - On login, call identify() to link anonymous → authenticated
 *   - On logout, call reset() to start fresh anonymous session
 *
 * FOR BEGINNERS:
 *   Mixpanel tracks user behavior to understand how people use XLR8.
 *   Events are actions (like "clicked button"), properties are context
 *   (like "which button" or "on which page"). Identity links all events
 *   to a specific user across sessions and devices.
 */

import mixpanel from "mixpanel-browser";

// Type definitions for our events
interface UserProperties {
  email?: string;
  artist_name?: string;
  role?: string;
  roles?: string[];
  created_at?: string;
  // Profile completion
  has_portfolio?: boolean;
  genres_count?: number;
}

interface EventProperties {
  [key: string]: string | number | boolean | string[] | null | undefined;
}

/**
 * Check if we're in development mode
 * Helps us log more info locally and skip tracking in tests
 */
const isDev = import.meta.env.DEV;
const isTest = import.meta.env.MODE === "test";

/**
 * Mixpanel token from environment
 * In Vite, env vars need the VITE_ prefix to be exposed to client code
 */
const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN;

/**
 * Whether analytics is properly configured
 * If no token, we gracefully degrade (log to console in dev, no-op in prod)
 */
const isConfigured = !!MIXPANEL_TOKEN && !isTest;

class AnalyticsService {
  private initialized = false;

  /**
   * Initialize Mixpanel SDK
   *
   * Call this once at app startup (in index.tsx).
   * Safe to call multiple times - will only initialize once.
   *
   * Config options explained:
   * - debug: Show Mixpanel logs in console (dev only)
   * - track_pageview: Auto-track page views (we'll do this manually for SPA)
   * - persistence: Use localStorage to persist identity across sessions
   * - ignore_dnt: Respect Do Not Track browser setting
   * - batch_requests: Batch events for better performance (prod only)
   */
  init(): void {
    if (this.initialized) {
      if (isDev) console.log("📊 Analytics already initialized");
      return;
    }

    if (!isConfigured) {
      if (isDev) {
        console.warn(
          "📊 Mixpanel not configured - set VITE_MIXPANEL_TOKEN in .env",
        );
        console.log("📊 Running in debug mode (events logged to console)");
      }
      this.initialized = true;
      return;
    }

    try {
      mixpanel.init(MIXPANEL_TOKEN, {
        debug: isDev,
        track_pageview: false, // We'll track page views manually for SPA routing
        persistence: "localStorage",
        ignore_dnt: false, // Respect user's Do Not Track preference
        batch_requests: !isDev, // Batch in production for performance
        api_host: "https://api.mixpanel.com", // Use Mixpanel's API directly
      });

      this.initialized = true;

      if (isDev) {
        console.log("📊 Mixpanel initialized successfully");
      }
    } catch (error) {
      console.error("📊 Failed to initialize Mixpanel:", error);
      // Don't throw - analytics failures shouldn't break the app
    }
  }

  /**
   * Identify a user (call on login/signup)
   *
   * CRITICAL: This links the anonymous device_id to the authenticated user_id.
   * All past anonymous events will be attributed to this user.
   *
   * @param userId - Unique user ID (use the Django user.id)
   * @param properties - User profile properties to set
   *
   * Example:
   *   analytics.identify(user.id, {
   *     email: user.email,
   *     artist_name: user.artist_name,
   *     role: user.role,
   *   });
   */
  identify(userId: string, properties?: UserProperties): void {
    if (!this.initialized) {
      this.init();
    }

    if (!isConfigured) {
      if (isDev) {
        console.log("📊 [DEBUG] identify:", userId, properties);
      }
      return;
    }

    try {
      // Link anonymous to identified
      mixpanel.identify(userId);

      // Set user properties (creates profile if doesn't exist)
      if (properties) {
        mixpanel.people.set(properties);
      }

      if (isDev) {
        console.log("📊 User identified:", userId);
      }
    } catch (error) {
      console.error("📊 Failed to identify user:", error);
    }
  }

  /**
   * Reset identity (call on logout)
   *
   * This clears the identified user and generates a new anonymous ID.
   * Important for shared devices or when switching accounts.
   */
  reset(): void {
    if (!isConfigured) {
      if (isDev) {
        console.log("📊 [DEBUG] reset identity");
      }
      return;
    }

    try {
      mixpanel.reset();

      if (isDev) {
        console.log("📊 Identity reset (new anonymous session)");
      }
    } catch (error) {
      console.error("📊 Failed to reset identity:", error);
    }
  }

  /**
   * Track an event
   *
   * Fire-and-forget: never throws, never blocks.
   *
   * @param eventName - Name of the event (use Title Case, e.g., "Request Created")
   * @param properties - Event-specific data
   *
   * Examples:
   *   analytics.track('Request Created', { genre: 'Hip Hop', mood: 'dark' });
   *   analytics.track('Submission Viewed', { submission_id: '123' });
   *   analytics.track('Contract Signed', { price: 500, rights_type: 'exclusive' });
   */
  track(eventName: string, properties?: EventProperties): void {
    if (!this.initialized) {
      this.init();
    }

    if (!isConfigured) {
      if (isDev) {
        console.log("📊 [DEBUG] track:", eventName, properties);
      }
      return;
    }

    try {
      mixpanel.track(eventName, properties);

      if (isDev) {
        console.log("📊 Event tracked:", eventName, properties);
      }
    } catch (error) {
      console.error("📊 Failed to track event:", error);
      // Never throw - analytics failures shouldn't break the app
    }
  }

  /**
   * Track a page view (for SPA navigation)
   *
   * Call this on route changes to track page views.
   * Automatically includes the current URL.
   *
   * @param pageName - Friendly name for the page (e.g., "Dashboard", "Request Detail")
   * @param properties - Additional properties
   */
  trackPageView(pageName: string, properties?: EventProperties): void {
    this.track("Page Viewed", {
      page_name: pageName,
      url: window.location.href,
      path: window.location.pathname,
      ...properties,
    });
  }

  /**
   * Set user properties without identifying
   *
   * Use this to update user properties after initial identification.
   * For example, when a user completes their profile.
   *
   * @param properties - Properties to set/update
   */
  setUserProperties(properties: UserProperties): void {
    if (!isConfigured) {
      if (isDev) {
        console.log("📊 [DEBUG] setUserProperties:", properties);
      }
      return;
    }

    try {
      mixpanel.people.set(properties);
    } catch (error) {
      console.error("📊 Failed to set user properties:", error);
    }
  }

  /**
   * Increment a numeric property
   *
   * Useful for tracking counts like "beats_uploaded" or "requests_created".
   *
   * @param property - Property name to increment
   * @param amount - Amount to increment by (default: 1)
   */
  incrementProperty(property: string, amount = 1): void {
    if (!isConfigured) {
      if (isDev) {
        console.log("📊 [DEBUG] increment:", property, amount);
      }
      return;
    }

    try {
      mixpanel.people.increment(property, amount);
    } catch (error) {
      console.error("📊 Failed to increment property:", error);
    }
  }

  /**
   * Time an event (start timing)
   *
   * Call this before an action, then call track() with the same event name
   * when the action completes. Mixpanel will automatically calculate duration.
   *
   * @param eventName - Event name to time
   *
   * Example:
   *   analytics.timeEvent('Beat Upload');
   *   // ... upload happens ...
   *   analytics.track('Beat Upload', { file_size: 1024 }); // Duration auto-added
   */
  timeEvent(eventName: string): void {
    if (!isConfigured) {
      if (isDev) {
        console.log("📊 [DEBUG] timeEvent:", eventName);
      }
      return;
    }

    try {
      mixpanel.time_event(eventName);
    } catch (error) {
      console.error("📊 Failed to time event:", error);
    }
  }

  /**
   * Register super properties
   *
   * Super properties are sent with EVERY event automatically.
   * Useful for things like "current_role" or "app_version".
   *
   * @param properties - Properties to register
   */
  registerSuperProperties(properties: EventProperties): void {
    if (!isConfigured) {
      if (isDev) {
        console.log("📊 [DEBUG] register super:", properties);
      }
      return;
    }

    try {
      mixpanel.register(properties);
    } catch (error) {
      console.error("📊 Failed to register super properties:", error);
    }
  }
}

// Export singleton instance
const analytics = new AnalyticsService();
export default analytics;

// Also export common event names as constants for consistency
export const AnalyticsEvents = {
  // Auth events
  SIGNUP_STARTED: "Signup Started",
  SIGNUP_COMPLETED: "Signup Completed",
  LOGIN: "Login",
  LOGOUT: "Logout",

  // Core funnel events (match backend tracking)
  REQUEST_CREATED: "Request Created",
  REQUEST_VIEWED: "Request Viewed",
  MATCH_VIEWED: "Match Viewed",
  SUBMISSION_CREATED: "Submission Created",
  SUBMISSION_VIEWED: "Submission Viewed",
  CONTRACT_DRAFT_CREATED: "Contract Draft Created",
  CONTRACT_SIGNED: "Contract Signed",

  // Engagement events
  BEAT_PLAYED: "Beat Played",
  BEAT_LIKED: "Beat Liked",
  MESSAGE_SENT: "Message Sent",
  PROFILE_VIEWED: "Profile Viewed",
  PROFILE_UPDATED: "Profile Updated",

  // Payment events
  PAYMENT_STARTED: "Payment Started",
  PAYMENT_COMPLETED: "Payment Completed",
  PAYMENT_FAILED: "Payment Failed",

  // Navigation events
  PAGE_VIEWED: "Page Viewed",
} as const;
